import { PERSONA_POLICY_REGISTRY } from '../access/persona-policy.registry.js';
import { CANONICAL_FINANCIAL_REPORTS, FINANCIAL_CAPABILITY_RESOURCE_REGISTRY, } from '../financial/index.js';
import { ORGANIZATION_AUDIT_EXPORT } from '../operations/operation-ids.js';
import { normalizeActionDescriptor } from './action.js';
import { OPERATION_IDS, ROUTE_IDS } from './routes/ids.js';
import { canonicalRouteRegistry, getRouteById } from './routes/effective-routes.js';
import { EXPERIENCE_FORM_REGISTRY } from './descriptors/effective-form-registry.js';
import { EXPERIENCE_RESOURCE_REGISTRY } from './descriptors/effective-resource-registry.js';
import { EXPERIENCE_TABLE_REGISTRY as BASE_EXPERIENCE_TABLE_REGISTRY } from './descriptors/action-aware-table-registry.js';
import { ORGANIZATION_BRANCHES_TABLE as BASE_ORGANIZATION_BRANCHES_TABLE } from './descriptors/effective-branch-descriptors.js';
import { EFFECTIVE_POSITION_TRANSFER_TABLE } from './descriptors/effective-position-transfer-table.js';
import { EFFECTIVE_DASHBOARD_REGISTRY } from './effective-dashboard-registry.js';
import { EFFECTIVE_REPORT_DASHBOARD_FORMS, EFFECTIVE_REPORT_REGISTRY, } from './effective-report-dashboard-registry.js';
function requireTable(id) {
    const table = BASE_EXPERIENCE_TABLE_REGISTRY[id];
    if (!table)
        throw new Error(`Missing canonical table ${id}`);
    return table;
}
function setupTable(table, options = {}) {
    const exportEnabled = options.export ?? table.capabilities?.export ?? false;
    const columnChooser = options.columnChooser ?? false;
    return {
        ...table,
        capabilities: {
            ...(table.capabilities ?? {}),
            grouping: false,
            aggregation: false,
            savedViews: false,
            columnVisibility: columnChooser,
            columnReordering: false,
            columnPinning: false,
            density: false,
            export: exportEnabled,
        },
        toolbar: {
            ...(table.toolbar ?? {}),
            showGrouping: false,
            showSavedViews: false,
            showColumnChooser: columnChooser,
            showDensity: false,
            showExport: exportEnabled,
        },
    };
}
function referenceDataTable(table) {
    return {
        ...table,
        capabilities: {
            ...(table.capabilities ?? {}),
            grouping: false,
            aggregation: false,
            savedViews: false,
            columnReordering: false,
            density: false,
        },
        toolbar: {
            ...(table.toolbar ?? {}),
            showGrouping: false,
            showSavedViews: false,
            showDensity: false,
        },
    };
}
function cloneTransactionColumn(template, field, labelKey, overrides = {}) {
    return {
        ...template,
        id: `column.transaction.${field}`,
        telemetryId: `experience.table.transaction.${field}`,
        accessibilityLabelKey: labelKey,
        field,
        labelKey: labelKey,
        ...overrides,
    };
}
const baseOrganizationUsersTable = requireTable('table.admin.users');
const ORGANIZATION_USERS_TABLE = Object.freeze(setupTable({
    ...baseOrganizationUsersTable,
    capabilities: {
        ...(baseOrganizationUsersTable.capabilities ?? {}),
        export: false,
    },
    toolbar: {
        ...(baseOrganizationUsersTable.toolbar ?? {}),
        showExport: false,
    },
    export: undefined,
}, { export: false, columnChooser: true }));
const baseRolesTable = requireTable('table.admin.roles');
const ORGANIZATION_ROLES_TABLE = Object.freeze(setupTable({
    ...baseRolesTable,
    columns: baseRolesTable.columns.map((column) => column.field === 'userTypeId'
        ? {
            ...column,
            field: 'code',
            labelKey: 'col.code',
            filterable: false,
            searchable: true,
        }
        : column),
}, { export: false, columnChooser: true }));
const ORGANIZATION_BRANCHES_TABLE = Object.freeze(setupTable(BASE_ORGANIZATION_BRANCHES_TABLE, { export: false, columnChooser: true }));
const ORGANIZATION_CLIENTS_TABLE = Object.freeze(setupTable(requireTable('table.admin.organizationClients'), { columnChooser: true }));
const ORGANIZATION_CLIENT_ACCOUNTS_TABLE = Object.freeze(setupTable(requireTable('table.admin.organizationClientAccounts'), { columnChooser: true }));
const ORGANIZATION_PORTFOLIOS_TABLE = Object.freeze(setupTable(requireTable('table.admin.organizationPortfolios'), { columnChooser: true }));
const TAX_RULE_TABLE = Object.freeze(setupTable(requireTable('table.admin.taxRules'), { columnChooser: true }));
const FEE_RULE_TABLE = Object.freeze(setupTable(requireTable('table.admin.feeRules'), { columnChooser: true }));
const INSTRUMENT_TABLE = Object.freeze(referenceDataTable(requireTable('table.data.instruments')));
const MARKET_PRICES_TABLE = Object.freeze(referenceDataTable(requireTable('table.data.marketPrices')));
const baseTransactionTable = requireTable('table.investment.transactions');
const transactionClientId = baseTransactionTable.columns.find((column) => column.field === 'clientAccountId');
const transactionPortfolioId = baseTransactionTable.columns.find((column) => column.field === 'portfolioAccountId');
const transactionTotalAmount = baseTransactionTable.columns.find((column) => column.field === 'totalAmount');
const transactionStatus = baseTransactionTable.columns.find((column) => column.field === 'status');
const transactionClientNameColumn = cloneTransactionColumn(transactionClientId, 'clientAccountName', 'col.clientAccountName', {
    defaultVisible: true,
    requiredVisible: true,
    searchable: true,
    filterable: false,
    defaultWidth: 200,
    privacyClassification: 'CONFIDENTIAL',
});
const transactionPortfolioNumberColumn = cloneTransactionColumn(transactionPortfolioId, 'portfolioAccountNumber', 'col.portfolioAccountNumber', {
    defaultVisible: true,
    searchable: true,
    filterable: false,
    defaultWidth: 170,
    privacyClassification: 'CONFIDENTIAL',
});
const transactionGrossAmountColumn = cloneTransactionColumn(transactionTotalAmount, 'grossAmount', 'col.grossAmount', { defaultVisible: false, sortable: true });
const transactionTotalChargesColumn = cloneTransactionColumn(transactionTotalAmount, 'totalCharges', 'col.totalCharges', { defaultVisible: false, sortable: false });
const transactionPostingStatusColumn = cloneTransactionColumn(transactionStatus, 'postingStatus', 'col.postingStatus', {
    defaultVisible: true,
    sortable: true,
    filterable: false,
    groupable: false,
    enumOptions: ['NOT_POSTED', 'POSTING', 'POSTED', 'FAILED', 'REVERSED'].map((value) => ({
        value,
        labelKey: `enum.postingStatus.${value}`,
    })),
});
const transactionColumns = [];
for (const column of baseTransactionTable.columns) {
    transactionColumns.push(column);
    if (column.field === 'currency') {
        transactionColumns.push(transactionClientNameColumn, transactionPortfolioNumberColumn);
    }
    if (column.field === 'totalAmount') {
        transactionColumns.push(transactionGrossAmountColumn, transactionTotalChargesColumn);
    }
    if (column.field === 'status') {
        transactionColumns.push(transactionPostingStatusColumn);
    }
}
const TRANSACTION_TABLE = Object.freeze({
    ...baseTransactionTable,
    columns: transactionColumns,
});
const baseOpeningCutoverTable = requireTable('table.admin.openingCutover');
const openingHistoryRoute = getRouteById(ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY);
const OPENING_CUTOVER_HISTORY_ACTION = {
    id: 'action.openingCutover.history',
    schemaVersion: baseOpeningCutoverTable.schemaVersion,
    module: baseOpeningCutoverTable.module,
    feature: baseOpeningCutoverTable.feature,
    privacyClassification: baseOpeningCutoverTable.privacyClassification,
    telemetryId: `${String(baseOpeningCutoverTable.telemetryId)}.history`,
    accessibilityLabelKey: 'openingCutover.actions.history',
    availability: 'AVAILABLE',
    owner: baseOpeningCutoverTable.owner,
    operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_GET,
    labelKey: 'openingCutover.actions.history',
    iconKey: 'history',
    kind: 'navigate',
    intent: 'secondary',
    idempotencyRequired: false,
    successBehavior: 'NAVIGATE_TO',
    targetRouteId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY,
    successNavigateTo: openingHistoryRoute?.path ?? '/admin/opening-cutover/history',
    errorBehavior: 'SHOW_INLINE_ERROR',
    order: 30,
    group: 'navigation',
};
const OPENING_CUTOVER_TABLE = Object.freeze(setupTable({
    ...baseOpeningCutoverTable,
    pageActions: [
        ...(baseOpeningCutoverTable.pageActions ?? []).filter((action) => action.id !== OPENING_CUTOVER_HISTORY_ACTION.id),
        OPENING_CUTOVER_HISTORY_ACTION,
    ],
}, { columnChooser: true }));
const baseOrganizationAuditTable = requireTable('table.admin.audit');
const ORGANIZATION_AUDIT_TABLE = Object.freeze({
    ...baseOrganizationAuditTable,
    operationMode: 'client',
    serverSidePagination: false,
    serverSideSorting: false,
    serverSideFiltering: false,
    export: {
        formats: ['csv'],
        defaultFormat: 'csv',
        scopes: ['all'],
        defaultScope: 'all',
        formulaInjectionProtection: true,
        serverExport: {
            operationId: ORGANIZATION_AUDIT_EXPORT,
            endpoint: '/api/admin/audit-log/export',
            method: 'GET',
            filterParameterMap: {
                entityType: 'entityType',
                action: 'action',
                createdAt: 'dateFrom',
            },
            searchParameter: 'search',
        },
    },
    capabilities: {
        ...(baseOrganizationAuditTable.capabilities ?? {}),
        sorting: false,
        filtering: false,
        globalSearch: false,
        grouping: false,
        aggregation: false,
        pagination: false,
        export: true,
    },
    toolbar: {
        ...(baseOrganizationAuditTable.toolbar ?? {}),
        showGlobalSearch: false,
        showFilterRowToggle: false,
        showActiveFilterSummary: false,
        showClearFilters: false,
        showGrouping: false,
        showResultCount: false,
        showExport: true,
    },
});
const baseImportJobTable = requireTable('table.data.importJobs');
const IMPORT_JOB_TABLE = Object.freeze({
    ...baseImportJobTable,
});
const EFFECTIVE_EXPERIENCE_TABLE_REGISTRY = Object.freeze({
    ...BASE_EXPERIENCE_TABLE_REGISTRY,
    [String(ORGANIZATION_BRANCHES_TABLE.id)]: ORGANIZATION_BRANCHES_TABLE,
    [String(ORGANIZATION_USERS_TABLE.id)]: ORGANIZATION_USERS_TABLE,
    [String(ORGANIZATION_ROLES_TABLE.id)]: ORGANIZATION_ROLES_TABLE,
    [String(ORGANIZATION_CLIENTS_TABLE.id)]: ORGANIZATION_CLIENTS_TABLE,
    [String(ORGANIZATION_CLIENT_ACCOUNTS_TABLE.id)]: ORGANIZATION_CLIENT_ACCOUNTS_TABLE,
    [String(ORGANIZATION_PORTFOLIOS_TABLE.id)]: ORGANIZATION_PORTFOLIOS_TABLE,
    [String(TRANSACTION_TABLE.id)]: TRANSACTION_TABLE,
    [String(EFFECTIVE_POSITION_TRANSFER_TABLE.id)]: EFFECTIVE_POSITION_TRANSFER_TABLE,
    // Compatibility key used by the route/page registry. The descriptor retains
    // its stable settlement namespace while consumers resolve the canonical
    // investment-table alias.
    'table.investment.settlementInstructions': requireTable('table.settlement.instructions'),
    [String(OPENING_CUTOVER_TABLE.id)]: OPENING_CUTOVER_TABLE,
    [String(TAX_RULE_TABLE.id)]: TAX_RULE_TABLE,
    [String(FEE_RULE_TABLE.id)]: FEE_RULE_TABLE,
    [String(INSTRUMENT_TABLE.id)]: INSTRUMENT_TABLE,
    [String(MARKET_PRICES_TABLE.id)]: MARKET_PRICES_TABLE,
    [String(ORGANIZATION_AUDIT_TABLE.id)]: ORGANIZATION_AUDIT_TABLE,
    'table.admin.organizationAudit': ORGANIZATION_AUDIT_TABLE,
    'table.admin.platformHealth': ORGANIZATION_AUDIT_TABLE,
    'table.admin.platformSupportDelegations': ORGANIZATION_AUDIT_TABLE,
    [String(IMPORT_JOB_TABLE.id)]: IMPORT_JOB_TABLE,
});
function registerCanonicalAction(actions, action) {
    const normalized = normalizeActionDescriptor(action);
    const id = String(normalized.id);
    if (actions[id])
        return;
    actions[id] = normalized;
}
function collectActions() {
    const actions = {};
    for (const table of Object.values(EFFECTIVE_EXPERIENCE_TABLE_REGISTRY)) {
        for (const action of [
            ...(table.rowActions ?? []),
            ...(table.bulkActions ?? []),
            ...(table.pageActions ?? []),
            ...(table.createAction ? [table.createAction] : []),
            ...(table.rowActivationAction ? [table.rowActivationAction] : []),
        ])
            registerCanonicalAction(actions, action);
    }
    for (const form of Object.values(EXPERIENCE_FORM_REGISTRY)) {
        for (const action of form.actions)
            registerCanonicalAction(actions, action);
    }
    for (const form of Object.values(EFFECTIVE_REPORT_DASHBOARD_FORMS)) {
        for (const action of form.actions)
            registerCanonicalAction(actions, action);
    }
    return Object.freeze(actions);
}
export const CANONICAL_EXPERIENCE_REGISTRY = Object.freeze({
    schemaVersion: 2,
    registryVersion: 'experience-v2-portfolio-nepal-evidence',
    routes: Object.freeze([...canonicalRouteRegistry]),
    resources: EXPERIENCE_RESOURCE_REGISTRY,
    tables: EFFECTIVE_EXPERIENCE_TABLE_REGISTRY,
    forms: Object.freeze({ ...EXPERIENCE_FORM_REGISTRY, ...EFFECTIVE_REPORT_DASHBOARD_FORMS }),
    actions: collectActions(),
    personas: PERSONA_POLICY_REGISTRY,
    reports: EFFECTIVE_REPORT_REGISTRY,
    dashboards: EFFECTIVE_DASHBOARD_REGISTRY,
    financialCapabilities: FINANCIAL_CAPABILITY_RESOURCE_REGISTRY,
    financialReportTaxonomy: CANONICAL_FINANCIAL_REPORTS,
});
export function getCanonicalExperienceRegistry() {
    return CANONICAL_EXPERIENCE_REGISTRY;
}
